/**
 * Utility functions for scrolling to specific sections
 */

// Function to find section position with fallbacks
const findSectionPosition = (sectionId, offsetY = 120) => {
  console.log(`Finding position for section: ${sectionId}`);
  
  // Different query strategies for each section type
  let element = null;
  
  if (sectionId === 'workshops') {
    // Try multiple selectors for workshops section
    element = document.querySelector('.workshops-section') || 
              document.querySelector('.workshops-hero') ||
              document.getElementById('workshops');
  } else if (sectionId === 'instructors') {
    // Try multiple selectors for instructors section
    element = document.querySelector('.instructors-section') || 
              document.querySelector('.our-instructors-section') ||
              document.querySelector('.instructor-slider-container') ||
              document.getElementById('instructors');
  } else if (sectionId === 'schedule') {
    // Try multiple selectors for schedule section
    element = document.querySelector('.schedule-section') ||
              document.getElementById('schedule');
  } else if (sectionId === 'about') {
    // Try multiple selectors for about section
    element = document.querySelector('.about-section') ||
              document.getElementById('about-us') ||
              document.getElementById('about');
  } else {
    // General case - try ID first
    element = document.getElementById(sectionId);
  }
  
  // If element was found, calculate position
  if (element) {
    const rect = element.getBoundingClientRect();
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    return scrollPosition + rect.top - offsetY;
  }
  
  return null;
};

// Get section position with fixed fallbacks
const getSectionPosition = (sectionId, fallbackPosition = 0, offsetY = 120) => {
  // Map of fallback positions for common sections
  const knownPositions = {
    'workshops': 2300,
    'instructors': 1000,
    'schedule': 500,
    'about': 3500,
    'about-us': 3500
  };
  
  // Try to find the position directly
  const position = findSectionPosition(sectionId, offsetY);
  
  if (position !== null) {
    console.log(`Found ${sectionId} section at position: ${position}`);
    return position;
  }
  
  // If not found, use known position or fallback
  const knownPosition = knownPositions[sectionId];
  if (knownPosition) {
    console.log(`Using known position for ${sectionId}: ${knownPosition}`);
    return knownPosition;
  }
  
  console.log(`Using fallback position for ${sectionId}: ${fallbackPosition}`);
  return fallbackPosition;
};

// Improved function to scroll to a section
export const scrollToSection = (sectionId, fallbackPosition = 0, offsetY = 120) => {
  // Increase offset for instructors section to prevent header overlap
  if (sectionId === 'instructors') {
    offsetY = 180; // Increased offset for instructors section
  }

  // Set up multiple attempts to ensure page is loaded
  const attemptScroll = (attempt = 1, maxAttempts = 5) => {
    if (attempt > maxAttempts) {
      console.log(`Maximum attempts reached, using fallback position: ${fallbackPosition}`);
      window.scrollTo({
        top: fallbackPosition,
        behavior: 'smooth'
      });
      return;
    }
    
    try {
      console.log(`Scroll attempt ${attempt} for ${sectionId}`);
      
      // Adjusted mapping for special cases
      const targetId = sectionId === 'about' ? 'about-us' : sectionId;
      
      // Get position with progressively increased wait times
      const position = getSectionPosition(targetId, fallbackPosition, offsetY);
      
      if (position) {
        window.scrollTo({
          top: position,
          behavior: 'smooth'
        });
        
        // Double-check position after scroll animation
        setTimeout(() => {
          const newPosition = getSectionPosition(targetId, fallbackPosition, offsetY);
          if (newPosition && Math.abs(window.scrollY - newPosition) > 50) {
            window.scrollTo({
              top: newPosition,
              behavior: 'smooth'
            });
          }
        }, 700);
      } else if (attempt < maxAttempts) {
        // Try again with exponential backoff
        const delay = 300 * Math.pow(1.5, attempt);
        console.log(`Section not found, retrying in ${delay}ms...`);
        setTimeout(() => attemptScroll(attempt + 1, maxAttempts), delay);
      }
    } catch (error) {
      console.error('Error during scroll attempt:', error);
      if (attempt < maxAttempts) {
        setTimeout(() => attemptScroll(attempt + 1, maxAttempts), 300 * attempt);
      } else {
        // Last resort fallback
        window.scrollTo({
          top: fallbackPosition,
          behavior: 'smooth'
        });
      }
    }
  };
  
  // Start the first attempt after a short delay
  setTimeout(() => attemptScroll(), 200);
};

/**
 * Scrolls to a specific section on the main page
 * 
 * @param {string} sectionId - The ID of the section to scroll to
 * @param {number} fallbackPosition - Fallback scroll position if section not found (in px)
 * @param {number} offsetY - Offset from the top (useful for fixed headers)
 */
export const navigateToMainPageSection = (sectionId, fallbackPosition = 1000, offsetY = 120) => {
  // Increase offset for instructors section to prevent header overlap
  if (sectionId === 'instructors') {
    offsetY = 180; // Increased offset for instructors section
  }

  // First, navigate to the home page if we're not already there
  const currentPath = window.location.pathname;
  if (currentPath !== '/') {
    // Store the navigation intent with timestamp for the page to pick up after redirect
    localStorage.setItem('scroll_target', sectionId);
    localStorage.setItem('scroll_position', fallbackPosition.toString());
    localStorage.setItem('scroll_offset', offsetY.toString());
    localStorage.setItem('scroll_timestamp', Date.now().toString());
    
    window.location.href = '/#' + sectionId;
    return;
  }
  
  // Already on home page, update the hash without full page reload
  if (window.location.hash !== '#' + sectionId) {
    window.history.pushState(null, '', '/#' + sectionId);
  }
  
  // Function to try and find the section element with multiple selector strategies
  const findAndScrollToSection = (attemptsLeft = 6) => {
    // Try to find the section using multiple selectors
    let section = document.getElementById(sectionId);
    
    // Try alternative selectors based on section type
    if (!section) {
      if (sectionId === 'workshops') {
        section = document.querySelector('.workshops-section') || 
                document.querySelector('.workshops-hero');
      } else if (sectionId === 'instructors') {
        section = document.querySelector('.instructors-section') || 
                document.querySelector('.our-instructors-section') ||
                document.querySelector('.instructor-slider-container');
      } else if (sectionId === 'schedule') {
        section = document.querySelector('.schedule-section');
      } else if (sectionId === 'about') {
        section = document.getElementById('about-us') || 
                document.querySelector('.about-section');
      }
    }
    
    if (section) {
      // Calculate position accounting for the header
      const sectionPosition = section.getBoundingClientRect().top + window.scrollY;
      const targetPosition = sectionPosition - offsetY;
      
      // Scroll to position with smooth behavior
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      // Double-check position after scroll animation (after 800ms)
      setTimeout(() => {
        const currentPos = window.scrollY;
        const sectionPos = section.getBoundingClientRect().top + window.scrollY - offsetY;
        
        // If we're not close to where we should be, adjust again
        if (Math.abs(currentPos - sectionPos) > 50) {
          window.scrollTo({
            top: sectionPos,
            behavior: 'smooth'
          });
        }
        
        // And one more check to ensure we're in the right spot
        setTimeout(() => {
          const finalPos = window.scrollY;
          const finalSectionPos = section.getBoundingClientRect().top + window.scrollY - offsetY;
          
          if (Math.abs(finalPos - finalSectionPos) > 50) {
            window.scrollTo({
              top: finalSectionPos,
              behavior: 'smooth'
            });
          }
        }, 1000);
      }, 800);
      
      // Log success
      console.log(`Successfully scrolled to section: ${sectionId} at position ${targetPosition}px`);
      return;
    }
    
    // If we couldn't find the section and have attempts left, try again with exponential backoff
    if (attemptsLeft > 0) {
      const delay = 300 * Math.pow(1.5, 6 - attemptsLeft);
      console.log(`Section ${sectionId} not found, retrying in ${delay}ms... (${attemptsLeft} attempts left)`);
      setTimeout(() => findAndScrollToSection(attemptsLeft - 1), delay);
    } else {
      // If all attempts failed, use fallback position
      console.log(`Failed to find section ${sectionId}, using fallback position: ${fallbackPosition}px`);
      window.scrollTo({
        top: fallbackPosition,
        behavior: 'smooth'
      });
    }
  };
  
  // Add a delay to ensure the page has time to load the sections
  // Longer delay for better reliability
  setTimeout(() => findAndScrollToSection(), 800);
};

// Function to check if we need to scroll after page load
export const checkScrollAfterNavigation = () => {
  // Check if we have a stored navigation request
  const scrollTarget = localStorage.getItem('scroll_target');
  
  if (scrollTarget && window.location.pathname === '/') {
    const scrollPosition = parseInt(localStorage.getItem('scroll_position') || '0', 10);
    const scrollOffset = parseInt(localStorage.getItem('scroll_offset') || '120', 10);
    const timestamp = parseInt(localStorage.getItem('scroll_timestamp') || '0', 10);
    
    // Check if navigation request is fresh (less than 10 seconds old)
    const isRecent = (Date.now() - timestamp) < 10000;
    
    if (isRecent) {
      console.log(`Processing recent scroll request to: ${scrollTarget}`);
      
      // Clear storage to prevent future scrolls
      localStorage.removeItem('scroll_target');
      localStorage.removeItem('scroll_position');
      localStorage.removeItem('scroll_offset');
      localStorage.removeItem('scroll_timestamp');
      
      // Wait for page to be fully rendered before scrolling
      const executeScroll = () => {
        // Initial attempt
        scrollToSection(scrollTarget, scrollPosition, scrollOffset);
        
        // Secondary attempt after a longer delay to catch cases where first attempt might fail
        setTimeout(() => {
          scrollToSection(scrollTarget, scrollPosition, scrollOffset);
        }, 1500);
      };
      
      // Use DOMContentLoaded or load event depending on situation
      if (document.readyState === 'complete') {
        setTimeout(executeScroll, 500); // Extra delay even if page is "complete"
      } else {
        window.addEventListener('load', () => {
          setTimeout(executeScroll, 800);
        }, { once: true });
      }
      
      return true;
    } else {
      // Clean up old navigation requests
      localStorage.removeItem('scroll_target');
      localStorage.removeItem('scroll_position');
      localStorage.removeItem('scroll_offset');
      localStorage.removeItem('scroll_timestamp');
    }
  }
  
  return false;
}; 